/**
 * UCDP (Uppsala Conflict Data Program) data source.
 * Fetches georeferenced armed conflict events from the GED API.
 * Free academic API, no key required. CC BY 4.0.
 */

import type { Source, SourceMeta, ConfigField, SourceStatus, NormalizedEvent, Severity } from '../base-source';
import { getApiBase } from '@/app/api-router';

const SOURCE_ID = 'ucdp';

function deathsToSeverity(bestEstimate: number): Severity {
  if (bestEstimate >= 100) return 'critical';
  if (bestEstimate >= 25) return 'high';
  if (bestEstimate >= 5) return 'medium';
  if (bestEstimate >= 1) return 'low';
  return 'info';
}

interface UcdpEvent {
  id: number;
  relid: string;
  year: number;
  active_year: number;
  type_of_violence: number; // 1=state-based, 2=non-state, 3=one-sided
  conflict_name: string;
  dyad_name: string;
  side_a: string;
  side_b: string;
  country: string;
  region: string;
  latitude: number;
  longitude: number;
  date_start: string;
  date_end: string;
  deaths_a?: number;
  deaths_b?: number;
  deaths_civilians?: number;
  deaths_unknown?: number;
  best?: number;
  high?: number;
  low?: number;
  source_article?: string;
  source_office?: string;
  where_description?: string;
}

interface UcdpResponse {
  Result?: UcdpEvent[];
  TotalCount?: number;
  NextPageUrl?: string;
}

function violenceTypeLabel(type: number): string {
  switch (type) {
    case 1: return 'State-based conflict';
    case 2: return 'Non-state conflict';
    case 3: return 'One-sided violence';
    default: return 'Armed conflict';
  }
}

let status: SourceStatus = {
  state: 'initializing',
  eventCount: 0,
};

export const ucdpSource: Source = {
  id: SOURCE_ID,

  meta: {
    name: 'UCDP',
    description: 'Uppsala Conflict Data Program — armed conflict events',
    icon: 'crosshair',
    category: 'conflict',
    updateInterval: 3_600_000, // 1 hour
    requiresApiKey: false,
    dataLicense: 'CC BY 4.0',
    builtIn: true,
  } satisfies SourceMeta,

  configSchema: [] as ConfigField[],

  async initialize(): Promise<void> {
    status = { state: 'initializing', eventCount: 0 };
  },

  async start(): Promise<void> {
    status.state = 'active';
    console.log('[Sentinel] UCDP conflict data source started');
  },

  async stop(): Promise<void> {
    status.state = 'disabled';
  },

  async fetch(): Promise<NormalizedEvent[]> {
    try {
      const url = `${getApiBase()}/api/data/ucdp?pagesize=500`;
      const response = await fetch(url, { signal: AbortSignal.timeout(20_000) });

      if (!response.ok) {
        throw new Error(`UCDP API returned ${response.status}`);
      }

      const data: UcdpResponse = await response.json();

      if (!data.Result || !Array.isArray(data.Result)) {
        return [];
      }

      const events: NormalizedEvent[] = [];

      for (const item of data.Result) {
        const lat = item.latitude;
        const lng = item.longitude;

        if (!isFinite(lat) || !isFinite(lng)) continue;

        const bestEstimate = item.best ?? 0;
        const typeLabel = violenceTypeLabel(item.type_of_violence);
        const locationName = item.where_description ?? item.country;

        events.push({
          id: `ucdp-${item.id}`,
          source: SOURCE_ID,
          timestamp: item.date_start ?? new Date().toISOString(),
          type: 'conflict',
          title: `${typeLabel}: ${item.dyad_name}`,
          description: `${locationName}, ${item.country} — ${bestEstimate} fatalities (est.)`,
          severity: deathsToSeverity(bestEstimate),
          location: {
            lat,
            lng,
            country: item.country,
            name: locationName,
          },
          tags: ['ucdp', 'armed-conflict', `violence-type-${item.type_of_violence}`],
          links: [],
          raw: {
            ucdpId: item.id,
            relid: item.relid,
            conflictName: item.conflict_name,
            sideA: item.side_a,
            sideB: item.side_b,
            violenceType: item.type_of_violence,
            deaths: {
              best: item.best,
              low: item.low,
              high: item.high,
              civilians: item.deaths_civilians,
            },
            year: item.year,
            region: item.region,
          },
        });
      }

      status.state = 'active';
      status.lastFetch = new Date().toISOString();
      status.eventCount = events.length;
      return events;
    } catch (err) {
      status.state = 'error';
      status.lastError = err instanceof Error ? err.message : String(err);
      throw err;
    }
  },

  getStatus(): SourceStatus {
    return { ...status };
  },
};
